import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, useApi } from '@/api/client';
import { StatusPill } from '@/components/StatusPill';
import { fmtDate } from '@/lib/format';
import type { AiEffort, PromptTemplateDto, SkillFile } from '@/types';

/** Prefill for the Suggest form when proposing a new version of an existing skill. */
interface SuggestPrefill {
  name: string;
  description: string;
  systemPrompt: string;
  files: SkillFile[];
  suggestedSupplier: string;
  suggestedModelId: string;
  suggestedEffort: AiEffort | '';
  suggestedTemperature: string;
  suggestedMaxTokens: string;
  suggestedResponseAsJson: boolean;
}

function problemMessage(e: unknown): string {
  if (e instanceof ApiError) return e.detail ?? e.message;
  return e instanceof Error ? e.message : 'Something went wrong.';
}

export function Templates() {
  const api = useApi();
  // ['me'] is already populated by the app shell, so this reads from the cache.
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const isAdmin = me.data?.isAdmin ?? false;

  const templates = useQuery({ queryKey: ['prompt-templates'], queryFn: api.listPromptTemplates });
  const mine = useQuery({ queryKey: ['prompt-templates-mine'], queryFn: api.myPromptTemplates });

  const approved = (templates.data ?? []).filter(t => t.status === 'Approved');

  // "Suggest an update" prefills the Suggest form with an existing skill's content; reusing its
  // name is what the API treats as a new version. Remount the card (nonce) so its state resets.
  const suggestRef = useRef<HTMLDivElement | null>(null);
  const [prefill, setPrefill] = useState<(SuggestPrefill & { nonce: number }) | null>(null);

  const suggestUpdate = (t: PromptTemplateDto) => {
    setPrefill({
      name: t.name,
      description: t.description ?? '',
      systemPrompt: t.systemPrompt,
      files: t.files.map(f => ({ fileName: f.fileName, content: f.content })),
      suggestedSupplier: t.suggestedSupplier ?? '',
      suggestedModelId: t.suggestedModelId ?? '',
      suggestedEffort: t.suggestedEffort ?? '',
      suggestedTemperature: t.suggestedTemperature != null ? String(t.suggestedTemperature) : '',
      suggestedMaxTokens: t.suggestedMaxTokens != null ? String(t.suggestedMaxTokens) : '',
      suggestedResponseAsJson: t.suggestedResponseAsJson ?? false,
      nonce: Date.now()
    });
    setTimeout(() => suggestRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Skills</h1>
          <div className="sub">
            Curated instruction bundles — a system prompt plus optional reference documents and
            suggested settings — usable from the Playground, the API (promptTemplateName) and MCP
            clients. Skills are prompt composition, so they work with every model; only
            attachments, file tasks and effort vary by model (see the capability icons on models).
          </div>
        </div>
      </div>

      {isAdmin && <PendingReview />}

      <div className="card" style={{ marginBottom: 22 }}>
        <div className="card-header"><h3>Skill library</h3></div>
        {templates.error ? (
          <div className="card-body">
            <div className="error-banner" style={{ marginBottom: 0 }}>
              Could not load templates: {problemMessage(templates.error)}
            </div>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th className="num">Version</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {templates.isLoading ? (
                <tr><td colSpan={4} className="placeholder">Loading skills...</td></tr>
              ) : approved.length === 0 ? (
                <tr><td colSpan={4} className="placeholder">No approved skills yet. Suggest the first one below.</td></tr>
              ) : approved.map(t => <LibraryRow key={t.id} template={t} onSuggestUpdate={suggestUpdate} />)}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid-2" style={{ alignItems: 'start' }} ref={suggestRef}>
        <SuggestCard key={prefill?.nonce ?? 0} initial={prefill ?? undefined} />
        <MySuggestions
          rows={mine.data ?? []}
          isLoading={mine.isLoading}
          error={mine.error}
        />
      </div>
    </>
  );
}

function SystemPromptBlock({ text }: { text: string }) {
  return <div className="completion-output bounded" style={{ fontSize: '.88rem', maxHeight: 320 }}>{text}</div>;
}

/** Expanded skill view: prompt, reference files and suggested defaults. */
function SkillDetail({ template }: { template: PromptTemplateDto }) {
  const suggestions: string[] = [];
  if (template.suggestedSupplier && template.suggestedModelId) {
    suggestions.push(`model ${template.suggestedSupplier}/${template.suggestedModelId}`);
  }
  if (template.suggestedEffort) suggestions.push(`effort ${template.suggestedEffort}`);
  if (template.suggestedTemperature != null) suggestions.push(`temperature ${template.suggestedTemperature}`);
  if (template.suggestedMaxTokens != null) suggestions.push(`maxTokens ${template.suggestedMaxTokens}`);
  if (template.suggestedResponseAsJson != null) suggestions.push(`responseAsJson ${template.suggestedResponseAsJson}`);

  return (
    <div>
      <SystemPromptBlock text={template.systemPrompt} />
      {suggestions.length > 0 && (
        <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-700)', margin: '8px 0' }}>
          Suggested defaults: {suggestions.join(' · ')}
        </div>
      )}
      {template.files.map(f => (
        <details key={f.fileName} style={{ margin: '8px 0' }}>
          <summary style={{ cursor: 'pointer', fontSize: '.85rem' }}>📎 {f.fileName} ({(f.content.length / 1024).toFixed(1)} KB)</summary>
          <SystemPromptBlock text={f.content} />
        </details>
      ))}
    </div>
  );
}

function LibraryRow({ template, onSuggestUpdate }: {
  template: PromptTemplateDto;
  onSuggestUpdate: (t: PromptTemplateDto) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr>
        <td>
          <span className="row-title">{template.name}</span>
          {template.files.length > 0 && (
            <div className="row-sub">{template.files.length} reference file{template.files.length > 1 ? 's' : ''}</div>
          )}
        </td>
        <td style={{ maxWidth: 460 }}>{template.description || '—'}</td>
        <td className="num">v{template.version}</td>
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <button className="btn-ghost btn-sm" onClick={() => setOpen(o => !o)}>
            {open ? 'Hide' : 'View'}
          </button>{' '}
          <button
            className="btn-secondary btn-sm"
            title="Prefill the Suggest form with this skill to propose a new version"
            onClick={() => onSuggestUpdate(template)}>
            Suggest update
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4}><SkillDetail template={template} /></td>
        </tr>
      )}
    </>
  );
}

const suggestSuppliers = ['Anthropic', 'OpenAi', 'AzureOpenAi', 'DeepSeek', 'MiniMax', 'Kimi', 'Grok'];

function SuggestCard({ initial }: { initial?: SuggestPrefill }) {
  const api = useApi();
  const qc = useQueryClient();
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [systemPrompt, setSystemPrompt] = useState(initial?.systemPrompt ?? '');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<SkillFile[]>(initial?.files ?? []);
  const [sSupplier, setSSupplier] = useState(initial?.suggestedSupplier ?? '');
  const [sModelId, setSModelId] = useState(initial?.suggestedModelId ?? '');
  const [sEffort, setSEffort] = useState<AiEffort | ''>(initial?.suggestedEffort ?? '');
  const [sTemperature, setSTemperature] = useState(initial?.suggestedTemperature ?? '');
  const [sMaxTokens, setSMaxTokens] = useState(initial?.suggestedMaxTokens ?? '');
  const [sJson, setSJson] = useState(initial?.suggestedResponseAsJson ?? false);
  const [submitted, setSubmitted] = useState(false);

  const tempInvalid = sTemperature.trim() !== '' && (Number.isNaN(Number(sTemperature)) || Number(sTemperature) < 0 || Number(sTemperature) > 2);

  const suggest = useMutation({
    mutationFn: () =>
      api.suggestPromptTemplate({
        name: name.trim(),
        description: description.trim() || undefined,
        systemPrompt,
        notes: notes.trim() || undefined,
        files: files.filter(f => f.fileName.trim() && f.content.trim()).length
          ? files.filter(f => f.fileName.trim() && f.content.trim())
          : undefined,
        suggestedSupplier: sSupplier || undefined,
        suggestedModelId: sModelId.trim() || undefined,
        suggestedEffort: sEffort || undefined,
        suggestedTemperature: sTemperature.trim() === '' ? undefined : Number(sTemperature),
        suggestedMaxTokens: sMaxTokens.trim() === '' ? undefined : Number(sMaxTokens),
        suggestedResponseAsJson: sJson || undefined
      }),
    onSuccess: () => {
      setName('');
      setDescription('');
      setSystemPrompt('');
      setNotes('');
      setFiles([]);
      setSSupplier('');
      setSModelId('');
      setSEffort('');
      setSTemperature('');
      setSMaxTokens('');
      setSJson(false);
      setSubmitted(true);
      qc.invalidateQueries({ queryKey: ['prompt-templates-mine'] });
    }
  });

  const canSubmit = name.trim() !== '' && systemPrompt.trim() !== '' && !tempInvalid && !suggest.isPending;

  const setFile = (index: number, patch: Partial<SkillFile>) =>
    setFiles(prev => prev.map((f, i) => i === index ? { ...f, ...patch } : f));

  return (
    <div className="card">
      <div className="card-header"><h3>Suggest a skill</h3></div>
      <div className="card-body">
        {suggest.error && (
          <div className="error-banner">Could not submit suggestion: {problemMessage(suggest.error)}</div>
        )}
        {submitted && !suggest.error && (
          <div className="info-banner">Submitted for admin review</div>
        )}
        {initial && !submitted && (
          <div className="info-banner">
            Proposing a new version of "{initial.name}" — keeping the name is what makes this an update.
            Edit anything below and submit for review.
          </div>
        )}
        <div className="field">
          <label htmlFor="tpl-name">Name</label>
          <input
            id="tpl-name"
            type="text"
            placeholder="e.g. travel-policy-answers"
            value={name}
            onChange={e => { setName(e.target.value); setSubmitted(false); }}
          />
          <div className="hint">Reusing an existing skill's name suggests a new version of that skill.</div>
        </div>
        <div className="field">
          <label htmlFor="tpl-description">Description</label>
          <input
            id="tpl-description"
            type="text"
            placeholder="What is this skill for?"
            value={description}
            onChange={e => { setDescription(e.target.value); setSubmitted(false); }}
          />
        </div>
        <div className="field">
          <label htmlFor="tpl-system">Instructions (system prompt)</label>
          <textarea
            id="tpl-system"
            rows={6}
            placeholder="The instructions callers will get when they pick this skill..."
            value={systemPrompt}
            onChange={e => { setSystemPrompt(e.target.value); setSubmitted(false); }}
          />
        </div>

        <div className="field">
          <label>Reference documents (optional, composed into the prompt)</label>
          {files.map((f, i) => (
            <div key={i} style={{ border: '1px solid var(--kt-grey-200)', borderRadius: 6, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input
                  type="text"
                  style={{ flex: 1 }}
                  placeholder="File name, e.g. refund-policy.md"
                  value={f.fileName}
                  onChange={e => setFile(i, { fileName: e.target.value })}
                  aria-label={`Reference file ${i + 1} name`}
                />
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}>
                  Remove
                </button>
              </div>
              <textarea
                rows={4}
                placeholder="Paste the reference content (markdown works well). Max 100 KB."
                value={f.content}
                onChange={e => setFile(i, { content: e.target.value })}
                aria-label={`Reference file ${i + 1} content`}
              />
            </div>
          ))}
          {files.length < 10 && (
            <button type="button" className="btn-secondary btn-sm" onClick={() => setFiles(prev => [...prev, { fileName: '', content: '' }])}>
              Add reference document
            </button>
          )}
        </div>

        <details style={{ marginBottom: 14 }} open={initial != null}>
          <summary style={{ cursor: 'pointer', fontSize: '.88rem' }}>Suggested defaults (optional)</summary>
          <div className="field-row" style={{ marginTop: 10 }}>
            <div className="field">
              <label htmlFor="tpl-s-supplier">Suggested supplier</label>
              <select id="tpl-s-supplier" className="select" style={{ width: '100%' }} value={sSupplier} onChange={e => setSSupplier(e.target.value)}>
                <option value="">None</option>
                {suggestSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="tpl-s-model">Suggested model id</label>
              <input id="tpl-s-model" type="text" placeholder="e.g. claude-sonnet-4-5" value={sModelId} onChange={e => setSModelId(e.target.value)} />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="tpl-s-effort">Suggested effort</label>
              <select id="tpl-s-effort" className="select" style={{ width: '100%' }} value={sEffort} onChange={e => setSEffort(e.target.value as AiEffort | '')}>
                <option value="">None</option>
                {(['Low', 'Medium', 'High', 'XHigh', 'Max'] as AiEffort[]).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="tpl-s-max">Suggested max tokens</label>
              <input id="tpl-s-max" type="number" min={1} placeholder="e.g. 8192" value={sMaxTokens} onChange={e => setSMaxTokens(e.target.value)} />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="tpl-s-temp">Suggested temperature</label>
              <input
                id="tpl-s-temp"
                type="number"
                min={0}
                max={2}
                step={0.1}
                placeholder="0 – 2, e.g. 0.7"
                value={sTemperature}
                onChange={e => setSTemperature(e.target.value)}
              />
              {tempInvalid && <div className="hint" style={{ color: 'var(--kt-red)' }}>Temperature must be between 0 and 2.</div>}
            </div>
            <div className="field">
              <label htmlFor="tpl-s-json">Suggested response format</label>
              <label htmlFor="tpl-s-json" style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: '.88rem' }}>
                <input id="tpl-s-json" type="checkbox" checked={sJson} onChange={e => setSJson(e.target.checked)} />
                Ask the model for JSON (responseAsJson)
              </label>
            </div>
          </div>
          <div className="hint">Applied when a request using this skill leaves the setting unset; explicit values always win.</div>
        </details>

        <div className="field">
          <label htmlFor="tpl-notes">Notes for the reviewer (optional)</label>
          <textarea
            id="tpl-notes"
            rows={2}
            placeholder="Why is this skill useful?"
            value={notes}
            onChange={e => { setNotes(e.target.value); setSubmitted(false); }}
          />
        </div>
        <button className="btn-primary" onClick={() => suggest.mutate()} disabled={!canSubmit}>
          {suggest.isPending ? 'Submitting...' : 'Submit suggestion'}
        </button>
      </div>
    </div>
  );
}

function MySuggestions({ rows, isLoading, error }: {
  rows: PromptTemplateDto[];
  isLoading: boolean;
  error: unknown;
}) {
  return (
    <div className="card">
      <div className="card-header"><h3>My suggestions</h3></div>
      {error ? (
        <div className="card-body">
          <div className="error-banner" style={{ marginBottom: 0 }}>
            Could not load your suggestions: {problemMessage(error)}
          </div>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="num">Version</th>
              <th>Status</th>
              <th>Review notes</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="placeholder">Loading your suggestions...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="placeholder">You haven't suggested any skills yet.</td></tr>
            ) : rows.map(t => (
              <tr key={t.id}>
                <td><span className="row-title">{t.name}</span></td>
                <td className="num">v{t.version}</td>
                <td><StatusPill status={t.status} /></td>
                <td style={{ maxWidth: 260 }}>
                  {t.reviewNotes
                    ? t.reviewNotes
                    : <span style={{ color: 'var(--kt-grey-400)' }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PendingReview() {
  const api = useApi();
  const pending = useQuery({
    queryKey: ['prompt-templates-pending'],
    queryFn: api.adminPendingPromptTemplates
  });
  const rows = pending.data ?? [];

  return (
    <div className="card" style={{ marginBottom: 22 }}>
      <div className="card-header">
        <h3>Pending review</h3>
        {rows.length > 0 && <span className="pill pill-pending">{rows.length} waiting</span>}
      </div>
      {pending.error ? (
        <div className="card-body">
          <div className="error-banner" style={{ marginBottom: 0 }}>
            Could not load pending suggestions: {problemMessage(pending.error)}
          </div>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Suggested by</th>
              <th>Notes</th>
              <th>Prompt</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pending.isLoading ? (
              <tr><td colSpan={5} className="placeholder">Loading suggestions...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="placeholder">Nothing waiting for review.</td></tr>
            ) : rows.map(t => <PendingRow key={t.id} template={t} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PendingRow({ template }: { template: PromptTemplateDto }) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['prompt-templates-pending'] });
    qc.invalidateQueries({ queryKey: ['prompt-templates'] });
    qc.invalidateQueries({ queryKey: ['prompt-templates-mine'] });
  };

  const approve = useMutation({
    mutationFn: () => api.adminApprovePromptTemplate(template.id, notes.trim() || undefined),
    onSuccess: invalidate
  });
  const reject = useMutation({
    mutationFn: () => api.adminRejectPromptTemplate(template.id, notes.trim() || undefined),
    onSuccess: invalidate
  });

  const busy = approve.isPending || reject.isPending;
  const error = approve.error ?? reject.error;

  return (
    <>
      <tr>
        <td>
          <div className="row-title">{template.name}</div>
          <div className="row-sub">v{template.version}</div>
        </td>
        <td>
          {template.createdBy ?? '—'}
          <div className="row-sub" style={{ fontFamily: 'inherit' }}>{fmtDate(template.createdAtUtc)}</div>
        </td>
        <td style={{ maxWidth: 280 }}>
          {template.suggestionNotes || <span style={{ color: 'var(--kt-grey-400)' }}>None given</span>}
        </td>
        <td>
          <button className="btn-ghost btn-sm" onClick={() => setOpen(o => !o)}>
            {open ? 'Hide prompt' : 'View prompt'}
          </button>
        </td>
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          {error && (
            <div style={{ color: 'var(--kt-red)', fontSize: '.78rem', marginBottom: 4, whiteSpace: 'normal' }}>
              {problemMessage(error)}
            </div>
          )}
          <input
            className="cell-input"
            style={{ width: 170 }}
            type="text"
            placeholder="Review notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            aria-label={`Review notes for ${template.name}`}
          />
          <button
            className="btn-primary btn-sm"
            style={{ marginLeft: 8 }}
            onClick={() => approve.mutate()}
            disabled={busy}>
            {approve.isPending ? 'Approving...' : 'Approve'}
          </button>
          <button
            className="btn-danger btn-sm"
            style={{ marginLeft: 6 }}
            onClick={() => reject.mutate()}
            disabled={busy}>
            {reject.isPending ? 'Rejecting...' : 'Reject'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5}><SkillDetail template={template} /></td>
        </tr>
      )}
    </>
  );
}
